import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import axios from 'axios';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import PropTypes from 'prop-types';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function DashboardCard09({ stockSymbol }) {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
    currency: 'USD'
  });
  const [loading, setLoading] = useState(true);

  const formatFinancialNumber = (number) => {
    const absNumber = Math.abs(number);
    if (absNumber >= 1e12) return `${(number / 1e12).toFixed(2)}T`;
    if (absNumber >= 1e9) return `${(number / 1e9).toFixed(2)}B`;
    if (absNumber >= 1e6) return `${(number / 1e6).toFixed(2)}M`;
    return number.toFixed(2);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!stockSymbol) {
        console.error('No stockSymbol provided');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`https://investorverden.onrender.com/api/financials?q=${stockSymbol}`);
        if (response.data && response.data.financialData) {
          const fd = response.data.financialData;
          const detectedCurrency = fd.financialCurrency || 'USD';

          // Convert string values to numbers and remove any NaN values
          const rawData = [
            parseFloat(fd.totalCash) || 0,
            parseFloat(fd.freeCashflow) || 0,
            parseFloat(fd.totalDebt) || 0
          ];

          console.log('Raw financial data:', rawData); // Debug log

          setChartData({
            labels: ['Samlet likvide beholdninger', 'Frie pengestrømme', 'Samlet gæld'],
            datasets: [
              {
                label: 'Financial Metrics',
                data: rawData,
                backgroundColor: [
                  'rgba(75, 192, 192, 0.5)',
                  'rgba(54, 162, 235, 0.5)',
                  'rgba(255, 99, 132, 0.5)'
                ],
                hoverBackgroundColor: [
                  'rgba(75, 192, 192, 0.7)',
                  'rgba(54, 162, 235, 0.7)',
                  'rgba(255, 99, 132, 0.7)'
                ],
                barPercentage: 0.5,
                categoryPercentage: 0.8
              }
            ],
            currency: detectedCurrency
          });
        }
      } catch (error) {
        console.error('Error fetching financial data:', error);
      }
      setLoading(false);
    };

    fetchData();
  }, [stockSymbol]);

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    aspectRatio: window.innerWidth < 640 ? 1.2 : 2,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      y: {
        beginAtZero: true, // Ensure the chart starts from zero for better comparison
        display: false,
      },
      x: {
        display: false,
      },
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = formatFinancialNumber(context.raw);
            return `${chartData.labels[context.dataIndex]}: ${chartData.currency} ${value}`;
          }
        }
      },
      legend: {
        display: false
      },
      title: {
        display: true,
        text: `Finansiel sundhed (${chartData.currency})`,
        font: {
          size: window.innerWidth < 640 ? 14 : 16
        }
      }
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col col-span-full sm:col-span-6 bg-white dark:bg-gray-800 shadow-lg rounded-xl p-4 items-center justify-center" style={{ minHeight: '300px' }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col col-span-full sm:col-span-6 bg-white dark:bg-gray-800 shadow-lg rounded-xl p-2 sm:p-4">
      <header className="px-3 sm:px-5 py-3 sm:py-4 border-b border-gray-100 dark:border-gray-700/60">
        <h2 className="font-semibold text-gray-800 dark:text-gray-100 text-sm sm:text-base">
          Finansiel sundhed ({chartData.currency})
        </h2>
      </header>
      <div className="p-2 sm:p-4" style={{ height: window.innerWidth < 640 ? '250px' : '300px' }}>
        <Bar 
          data={{
            ...chartData,
            datasets: [{
              ...chartData.datasets[0],
              barPercentage: window.innerWidth < 640 ? 0.7 : 0.5,
              categoryPercentage: window.innerWidth < 640 ? 0.9 : 0.8
            }]
          }} 
          options={options} 
        />
      </div>
    </div>
  );
}

DashboardCard09.propTypes = {
  stockSymbol: PropTypes.string.isRequired,
};

export default DashboardCard09;